'use client';

import { c as _c } from "react/compiler-runtime";
import type { KeyboardShortcutEntry, KeyboardShortcutGroup } from '@/lib/keyboard-shortcuts/registry';
import { Search } from 'lucide-react';
import { usePathname } from 'next/navigation';
import { useMemo, useRef, useState } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';
import { useToDesktop } from '@/components/providers/todesktop-auth-provider';
import { KeyboardKey } from '@/components/ui/keyboard-key';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { useHasCodeAccess } from '@/data/code-sessions/use-has-code-access';
import { getKeyboardShortcutContext, getKeyboardShortcutsForPath, KEYBOARD_SHORTCUT_GROUP_ORDER } from '@/lib/keyboard-shortcuts/registry';
import { useKeyboardShortcutsDrawerStore } from '@/lib/stores/keyboard-shortcuts-drawer-store';
import { altChar, metaChar } from '@/lib/utils';
function getDisplayKey(key: string) {
  if (key === 'Mod') {
    return metaChar();
  }
  if (key === 'ControlOrSuper') {
    if (navigator.platform.includes('Mac')) {
      return '⌃';
    }
    return navigator.platform.startsWith('Win') ? 'Win' : 'Super';
  }
  if (key === 'Alt') {
    return altChar();
  }
  if (key === 'Shift') {
    return '⇧';
  }
  if (key === 'Enter') {
    return '↵';
  }
  return key;
}
function getSearchKey(key: string) {
  if (key === 'Mod') {
    return `${metaChar()} command cmd control ctrl mod`;
  }
  if (key === 'ControlOrSuper') {
    return `${getDisplayKey(key)} control ctrl super win`;
  }
  if (key === 'Alt') {
    return `${altChar()} option alt`;
  }
  if (key === 'Shift') {
    return 'shift ⇧';
  }
  if (key === 'Enter') {
    return 'enter return ↵';
  }
  return key;
}
function getSearchKeyAliases(key: string) {
  if (key === 'Mod') {
    return [metaChar(), 'command', 'cmd', 'control', 'ctrl', 'mod'];
  }
  if (key === 'ControlOrSuper') {
    return [getDisplayKey(key), 'control', 'ctrl', 'super', 'win'];
  }
  if (key === 'Alt') {
    return [altChar(), 'option', 'alt'];
  }
  if (key === 'Shift') {
    return ['⇧', 'shift'];
  }
  if (key === 'Enter') {
    return ['↵', 'enter', 'return'];
  }
  return [key];
}
function getSearchSequences(sequence: readonly string[]) {
  return sequence.map(getSearchKeyAliases).reduce<string[]>((aliases, keyAliases) => aliases.flatMap(alias => keyAliases.map(keyAlias => [alias, keyAlias].filter(Boolean).join(' '))), ['']);
}
function matchesSearch(entry: KeyboardShortcutEntry, searchTerm: string) {
  const normalizedSearch = searchTerm.trim().toLowerCase();
  if (!normalizedSearch) {
    return true;
  }
  const searchableText = [entry.label, entry.group, entry.scope, ...(entry.keywords ?? []), ...entry.keys.flatMap(sequence => sequence.map(getSearchKey)), ...entry.keys.flatMap(getSearchSequences)].join(' ').toLowerCase();
  return searchableText.includes(normalizedSearch);
}
function ShortcutSequence(t0) {
  const $ = _c(4);
  const {
    sequence
  } = t0;
  let t1;
  if ($[0] !== sequence) {
    t1 = sequence.map((key, index) => <KeyboardKey key={`${sequence.join("-")}-${index}`} className="h-5 min-w-5 rounded-[4px] border-zinc-700 bg-zinc-900 px-1.5 text-[11px] font-medium text-zinc-300 shadow-none">{getDisplayKey(key)}</KeyboardKey>);
    $[0] = sequence;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1) {
    t2 = <span className="inline-flex items-center gap-1">{t1}</span>;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return t2;
}
function ShortcutKeys(t0) {
  const $ = _c(7);
  const {
    entry
  } = t0;
  let t1;
  if ($[0] !== entry.id || $[1] !== entry.keys) {
    let t2;
    if ($[3] !== entry.id) {
      t2 = (sequence, index) => <span key={`${entry.id}-${sequence.join("-")}`} className="inline-flex items-center gap-1.5">{index > 0 ? <span className="text-[10px] text-zinc-600">or</span> : null}<ShortcutSequence sequence={sequence} /></span>;
      $[3] = entry.id;
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    t1 = entry.keys.map(t2);
    $[0] = entry.id;
    $[1] = entry.keys;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[5] !== t1) {
    t2 = <div className="ml-3 inline-flex shrink-0 items-center gap-1.5">{t1}</div>;
    $[5] = t1;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  return t2;
}
function ShortcutRow(t0) {
  const $ = _c(7);
  const {
    entry
  } = t0;
  let t1;
  if ($[0] !== entry.label) {
    t1 = <span className="min-w-0 truncate">{entry.label}</span>;
    $[0] = entry.label;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== entry) {
    t2 = <ShortcutKeys entry={entry} />;
    $[2] = entry;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = <div className="flex min-h-8 items-center justify-between gap-3 rounded-[4px] px-1.5 text-sm text-zinc-300">{t1}{t2}</div>;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
}
function groupShortcuts(entries: KeyboardShortcutEntry[], groupOrder: KeyboardShortcutGroup[]) {
  const grouped = new Map<KeyboardShortcutGroup, KeyboardShortcutEntry[]>();
  entries.forEach(entry => {
    const currentEntries = grouped.get(entry.group) ?? [];
    currentEntries.push(entry);
    grouped.set(entry.group, currentEntries);
  });
  return groupOrder.map(group => ({
    group,
    entries: grouped.get(group) ?? []
  })).filter(section => section.entries.length > 0);
}
export function KeyboardShortcutsDrawer() {
  const $ = _c(32);
  const pathname = usePathname() ?? "";
  const {
    isDesktopApp
  } = useToDesktop();
  const isOpen = useKeyboardShortcutsDrawerStore(_temp);
  const setOpen = useKeyboardShortcutsDrawerStore(_temp2);
  const [searchTerm, setSearchTerm] = useState("");
  const searchInputRef = useRef(null);
  let t0;
  if ($[0] !== setOpen) {
    t0 = () => setOpen(true);
    $[0] = setOpen;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      useKey: true,
      ignoreModifiers: true,
      ignoreEventWhen: _temp3,
      preventDefault: true,
      enableOnFormTags: false
    };
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  useHotkeys("?", t0, t1);
  let t2;
  if ($[3] !== pathname) {
    t2 = getKeyboardShortcutContext(pathname);
    $[3] = pathname;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const context = t2;
  const hasCodeAccess = useHasCodeAccess();
  let t3;
  if ($[5] !== context || $[6] !== hasCodeAccess || $[7] !== isDesktopApp || $[8] !== pathname || $[9] !== searchTerm) {
    let t4;
    if ($[11] !== searchTerm) {
      t4 = entry => matchesSearch(entry, searchTerm);
      $[11] = searchTerm;
      $[12] = t4;
    } else {
      t4 = $[12];
    }
    const entries = getKeyboardShortcutsForPath(pathname, {
      isDesktopApp,
      hasCodeAccess
    }).filter(t4);
    t3 = groupShortcuts(entries, KEYBOARD_SHORTCUT_GROUP_ORDER[context]);
    $[5] = context;
    $[6] = hasCodeAccess;
    $[7] = isDesktopApp;
    $[8] = pathname;
    $[9] = searchTerm;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  const sections = t3;
  let t4;
  if ($[13] !== sections) {
    t4 = sections.some(_temp4);
    $[13] = sections;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  const hasResults = t4;
  let t5;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = event_0 => {
      event_0.preventDefault();
      searchInputRef.current?.focus();
    };
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  let t6;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <SheetHeader className="sticky top-0 z-10 h-14 flex-row items-center justify-between space-y-0 border-b border-zinc-800 bg-zinc-950 px-5 text-left"><SheetTitle className="text-base font-medium tracking-normal text-zinc-100">Keyboard shortcuts</SheetTitle><KeyboardKey className="mr-7 h-5 min-w-5 rounded-[4px] border-zinc-800 bg-zinc-900 px-1.5 text-[10px] font-medium text-zinc-500 shadow-none">Esc</KeyboardKey></SheetHeader>;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  let t7;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <label className="sr-only" htmlFor="keyboard-shortcuts-search">Search shortcuts</label>;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  let t8;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <Search className="size-3.5 shrink-0" aria-hidden="true" />;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = event_1 => setSearchTerm(event_1.currentTarget.value);
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== searchTerm) {
    t10 = <div className="px-5 pb-4 pt-3">{t7}<div className="flex h-9 items-center gap-2 rounded-md border border-zinc-800 bg-zinc-900 px-2.5 text-zinc-500">{t8}<input ref={searchInputRef} id="keyboard-shortcuts-search" value={searchTerm} onChange={t9} placeholder="Search shortcuts" className="h-full min-w-0 flex-1 bg-transparent text-sm text-zinc-200 outline-none placeholder:text-zinc-600" /></div></div>;
    $[20] = searchTerm;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] !== hasResults || $[23] !== sections) {
    t11 = <div className="custom-scrollbar min-h-0 flex-1 overflow-y-auto px-5 pb-4 pt-1">{hasResults ? <div className="space-y-5">{sections.map(_temp6)}</div> : <div className="rounded-md border border-zinc-900 px-3 py-2 text-sm text-zinc-500">No shortcuts found</div>}</div>;
    $[22] = hasResults;
    $[23] = sections;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  let t12;
  if ($[25] !== t10 || $[26] !== t11) {
    t12 = <SheetContent aria-describedby={undefined} data-testid="keyboard-shortcuts-drawer" side="right" onOpenAutoFocus={t5} overlayClassName="z-[70] bg-black/45" className="z-[80] flex h-full w-full flex-col gap-0 overflow-hidden border-l border-zinc-800 bg-zinc-950 p-0 text-zinc-100 sm:max-w-[440px] lg:max-w-[480px] [&>button]:right-4 [&>button]:top-[18px] [&>button]:text-zinc-500 [&>button:hover]:text-zinc-200">{t6}{t10}{t11}</SheetContent>;
    $[25] = t10;
    $[26] = t11;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  let t13;
  if ($[28] !== isOpen || $[29] !== setOpen || $[30] !== t12) {
    t13 = <Sheet open={isOpen} onOpenChange={setOpen}>{t12}</Sheet>;
    $[28] = isOpen;
    $[29] = setOpen;
    $[30] = t12;
    $[31] = t13;
  } else {
    t13 = $[31];
  }
  return t13;
}
function _temp6(section_0) {
  return <section key={section_0.group} aria-labelledby={`shortcut-group-${section_0.group}`}><h3 id={`shortcut-group-${section_0.group}`} className="mb-2 text-[10px] font-medium uppercase tracking-[0.16em] text-zinc-500">{section_0.group}</h3><div className="space-y-1">{section_0.entries.map(_temp5)}</div></section>;
}
function _temp5(entry_0) {
  return <ShortcutRow key={entry_0.id} entry={entry_0} />;
}
function _temp4(section) {
  return section.entries.length > 0;
}
function _temp3(event) {
  return event.metaKey || event.ctrlKey && !event.altKey;
}
function _temp2(state_0) {
  return state_0.setOpen;
}
function _temp(state) {
  return state.isOpen;
}